import React, { useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VenueSubmissionModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    phone: '',
    website: '',
    category: '',
    description: '',
    accessibility_notes: '',
    overall_rating: '',
    // Accessibility features
    wheelchair_accessible_entrance: null,
    wheelchair_accessible_parking: null,
    wheelchair_accessible_restroom: null,
    wheelchair_accessible_seating: null,
    has_ramps: null,
    has_elevator: null,
    has_accessible_changing_table: null,
    has_quiet_spaces: null,
    allows_service_animals: null,
    has_sensory_accommodations: null
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const categories = [
    'Restaurant', 'Park', 'Library', 'Museum', 'Theater', 'Store', 
    'Medical', 'School', 'Recreation', 'Other'
  ];

  const states = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
  ];

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? (value ? parseInt(value) : '') : value
    }));
  };

  const handleAccessibilityChange = (feature, value) => {
    setFormData(prev => ({
      ...prev,
      [feature]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Please log in to submit a venue');
        setLoading(false);
        return;
      }

      // Clean up form data - convert empty strings to null for optional fields
      const cleanedData = { ...formData };
      Object.keys(cleanedData).forEach(key => {
        if (cleanedData[key] === '') {
          cleanedData[key] = null;
        }
      });

      const response = await axios.post(
        `${API_URL}/api/community/venues/submit`,
        cleanedData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      onSuccess('Venue submitted successfully! Thank you for contributing to the community.');
      onClose();
      
      // Reset form
      setFormData({
        name: '',
        address: '',
        city: '',
        state: '',
        zip_code: '',
        phone: '',
        website: '',
        category: '',
        description: '',
        accessibility_notes: '',
        overall_rating: '',
        wheelchair_accessible_entrance: null,
        wheelchair_accessible_parking: null,
        wheelchair_accessible_restroom: null,
        wheelchair_accessible_seating: null,
        has_ramps: null,
        has_elevator: null,
        has_accessible_changing_table: null,
        has_quiet_spaces: null,
        allows_service_animals: null,
        has_sensory_accommodations: null
      });

    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit venue. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const AccessibilityToggle = ({ feature, label, value, onChange }) => (
    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
      <span className="text-sm font-medium text-gray-700">{label}</span>
      <div className="flex space-x-2">
        <button
          type="button"
          onClick={() => onChange(feature, true)}
          className={`px-3 py-1 rounded text-sm ${
            value === true 
              ? 'bg-green-500 text-white' 
              : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
          }`}
        >
          Yes
        </button>
        <button
          type="button"
          onClick={() => onChange(feature, false)}
          className={`px-3 py-1 rounded text-sm ${
            value === false 
              ? 'bg-red-500 text-white' 
              : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
          }`}
        >
          No
        </button>
        <button
          type="button"
          onClick={() => onChange(feature, null)}
          className={`px-3 py-1 rounded text-sm ${
            value === null 
              ? 'bg-gray-500 text-white' 
              : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
          }`}
        >
          Unknown
        </button>
      </div>
    </div>
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800">Suggest a Venue</h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
            >
              ×
            </button>
          </div>

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Venue Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                  placeholder="e.g., Central Park Playground"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category
                </label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                >
                  <option value="">Select a category</option>
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Address */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Address *
              </label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                required
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                placeholder="Street address"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  City *
                </label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  required
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  State *
                </label>
                <select
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                  required
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                >
                  <option value="">Select state</option>
                  {states.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  ZIP Code *
                </label>
                <input
                  type="text"
                  name="zip_code"
                  value={formData.zip_code}
                  onChange={handleInputChange}
                  required
                  pattern="^\d{5}(-\d{4})?$"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                  placeholder="12345"
                />
              </div>
            </div>

            {/* Contact Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                  placeholder="(555) 123-4567"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Website
                </label>
                <input
                  type="url"
                  name="website"
                  value={formData.website}
                  onChange={handleInputChange}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                  placeholder="https://example.com"
                />
              </div>
            </div>

            {/* Accessibility Features */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Accessibility Features</h3>
              <div className="space-y-3">
                <AccessibilityToggle
                  feature="wheelchair_accessible_entrance"
                  label="Wheelchair Accessible Entrance"
                  value={formData.wheelchair_accessible_entrance}
                  onChange={handleAccessibilityChange}
                />
                <AccessibilityToggle
                  feature="wheelchair_accessible_parking"
                  label="Wheelchair Accessible Parking"
                  value={formData.wheelchair_accessible_parking}
                  onChange={handleAccessibilityChange}
                />
                <AccessibilityToggle
                  feature="wheelchair_accessible_restroom"
                  label="Wheelchair Accessible Restroom"
                  value={formData.wheelchair_accessible_restroom}
                  onChange={handleAccessibilityChange}
                />
                <AccessibilityToggle
                  feature="has_ramps"
                  label="Has Ramps"
                  value={formData.has_ramps}
                  onChange={handleAccessibilityChange}
                />
                <AccessibilityToggle
                  feature="has_elevator"
                  label="Has Elevator"
                  value={formData.has_elevator}
                  onChange={handleAccessibilityChange}
                />
                <AccessibilityToggle
                  feature="allows_service_animals"
                  label="Allows Service Animals"
                  value={formData.allows_service_animals}
                  onChange={handleAccessibilityChange}
                />
                <AccessibilityToggle
                  feature="has_quiet_spaces"
                  label="Has Quiet Spaces"
                  value={formData.has_quiet_spaces}
                  onChange={handleAccessibilityChange}
                />
                <AccessibilityToggle
                  feature="has_sensory_accommodations"
                  label="Has Sensory Accommodations"
                  value={formData.has_sensory_accommodations}
                  onChange={handleAccessibilityChange}
                />
              </div>
            </div>

            {/* Description and Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={3}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                placeholder="Tell us about this venue..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Accessibility Notes
              </label>
              <textarea
                name="accessibility_notes"
                value={formData.accessibility_notes}
                onChange={handleInputChange}
                rows={3}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                placeholder="Any specific accessibility information or tips..."
              />
            </div>

            {/* Rating */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Overall Rating
              </label>
              <select
                name="overall_rating"
                value={formData.overall_rating}
                onChange={handleInputChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
              >
                <option value="">Rate this venue</option>
                <option value="5">⭐⭐⭐⭐⭐ Excellent</option>
                <option value="4">⭐⭐⭐⭐ Very Good</option>
                <option value="3">⭐⭐⭐ Good</option>
                <option value="2">⭐⭐ Fair</option>
                <option value="1">⭐ Poor</option>
              </select>
            </div>

            {/* Submit Buttons */}
            <div className="flex justify-end space-x-4 pt-6">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Submitting...' : 'Submit Venue'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default VenueSubmissionModal;